import { audio } from "./AudioManager.js";

export class SwipeController {

  constructor(economy, uiElements, uiManager) {
    this.economy = economy;
    this.ui = uiElements;
    this.uiManager = uiManager;
    this.currentCase = null;
    this.evidenceStack = [];
    this.currentIndex = 0;
    this.keptIds = [];
    this.insight = 0;
    this.noise = 0;
    this.earnedXP = 0;
    this.correctTriads = 0;
    this.isSimulationActive = false;
    
    this.setupListeners();
  }

  setupListeners() {
    document.getElementById('swipe-discard').addEventListener('click', () => this.handleAction('discard'));
    document.getElementById('swipe-keep').addEventListener('click', () => this.handleAction('keep'));
    
    window.addEventListener('keydown', (e) => {
      if (!this.isSimulationActive) return;
      if (e.key.toLowerCase() === 'a') this.handleAction('discard');
      if (e.key.toLowerCase() === 'd') this.handleAction('keep');
    });

    // Touch Support (Optimized for all mobile ranges)
    let touchStartX = 0;
    let touchStartY = 0;
    const stack = document.getElementById('swipe-card-stack');
    
    // Non-passive listeners allow preventDefault() to block system scroll [cite: 5, 9]
    stack.addEventListener('touchstart', (e) => {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
    }, { passive: true });

    stack.addEventListener('touchmove', (e) => {
        if (!this.isSimulationActive) return;
        
        const currentX = e.touches[0].clientX;
        const currentY = e.touches[0].clientY;
        const deltaX = Math.abs(currentX - touchStartX);
        const deltaY = Math.abs(currentY - touchStartY);

        // If swipe is primarily horizontal, block vertical page scroll
        if (deltaX > 10 && deltaX > deltaY) {
            if (e.cancelable) e.preventDefault();
        }
    }, { passive: false });

    stack.addEventListener('touchend', (e) => {
      if (!this.isSimulationActive || e.changedTouches.length === 0) return;
      
      const touchEndX = e.changedTouches[0].clientX;
      const deltaX = touchEndX - touchStartX;
      const threshold = 60; // Slightly higher threshold for deliberate intent

      if (deltaX > threshold) this.handleAction('keep');
      else if (deltaX < -threshold) this.handleAction('discard');
    });

    stack.addEventListener('touchcancel', () => {
        touchStartX = 0;
        touchStartY = 0;
    });
  }

  spawnParticles(x, y, color) {
    const parent = document.getElementById('swipe-area');
    if (!parent) return;

    for (let i = 0; i < 15; i++) {
      const p = document.createElement('div');
      p.className = 'particle';
      const size = Math.random() * 8 + 4;
      p.style.width = `${size}px`;
      p.style.height = `${size}px`;
      p.style.backgroundColor = color;
      
      const angle = Math.random() * Math.PI * 2;
      const velocity = Math.random() * 80 + 30;
      const tx = Math.cos(angle) * velocity;
      const ty = Math.sin(angle) * velocity;
      
      p.style.left = `${x}px`;
      p.style.top = `${y}px`;
      p.style.setProperty('--tx', `${tx}px`);
      p.style.setProperty('--ty', `${ty}px`);
      
      parent.appendChild(p);
      setTimeout(() => p.remove(), 600);
    }
  }

  handleAction(action) {
    if (!this.isSimulationActive || (this.currentCase && this.currentIndex >= this.evidenceStack.length)) return;

    const currentEvidence = this.evidenceStack[this.currentIndex];
    const cardEl = document.querySelector('.swipe-card:last-child');
    if (!cardEl) return;
    
    const rect = cardEl.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;

    const isNoise = currentEvidence.noise_type !== 'none';
    
    if (action === 'keep') {
      cardEl.classList.add('swiping-right');
      this.keptIds.push(currentEvidence.evidence_id);
      if (!isNoise) this.insight++;
      else this.noise++;
      this.spawnParticles(rect.right, midY, 'var(--primary)');
      audio.play('click_success');
      this.hapticFeedback('light');
    } else {
      cardEl.classList.add('swiping-left');
      if (isNoise) {
          this.insight++; 
          this.hapticFeedback('double');
      } else {
          this.hapticFeedback('light');
      }
      this.spawnParticles(rect.left, midY, 'var(--detail)');
      audio.play('click_neutral');
    }

    setTimeout(() => {
      this.currentIndex++;
      this.render();
      this.checkCheckpoints();
    }, 300);
  }

  /**
   * Main entry point for starting a Dr. Swipe simulation.
   */
  async startSimulation(caseId, onComplete) {
    this.onSimulationComplete = onComplete;
    try {
      const res = await fetch(`./cases/${caseId}.json`);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      this.currentCase = await res.json();
      
      this.evidenceStack = [...this.currentCase.evidence_stream];
      this.currentIndex = 0;
      this.keptIds = [];
      this.noise = 0;
      this.earnedXP = 0;
      this.correctTriads = 0;
      this.isSimulationActive = true;
      
      this.render();
      audio.play('ambient_hospital');
    } catch (err) {
      console.error("Failed to load case:", err);
      if (onComplete) onComplete();
    }
  }

  // --- REWARD & LOGIC LOGIC [cite: 1, 9] ---

  validateQuizAnswer(quiz, selectedIndex) {
    const isCorrect = selectedIndex === quiz.correct_index;
    const requiredIds = quiz.required_evidence_ids || [];
    const hasAllRequired = requiredIds.every(id => this.keptIds.includes(id));
    
    let xpGain = 0;
    let feedbackMsg = "";
    let type = "info";

    if (isCorrect) {
        xpGain = 50; // Base XP for correct answer
        if (hasAllRequired) {
            xpGain += 50; // Intuition Bonus
            feedbackMsg = "✨ ¡Correcto! Bono de Intuición Médica otorgado por fundamentar tu decisión con evidencia.";
            type = "success";
        } else {
            const missing = requiredIds.filter(id => !this.keptIds.includes(id));
            feedbackMsg = `✅ Correcto, pero fue por suerte. Te faltó revisar evidencia clave: ${missing.join(', ')}.`;
            type = "info";
        }
    } else {
        if (hasAllRequired) {
            xpGain = 25; // Effort Bonus
            feedbackMsg = "📚 Respuesta incorrecta, pero valoramos que recopilaste toda la evidencia clave. +25 XP por esfuerzo.";
            type = "warning";
        } else {
            feedbackMsg = "❌ Incorrecto. No lograste integrar la información necesaria para este hito.";
            type = "error";
        }
    }

    this.earnedXP += xpGain;
    this.showQuizFeedback(feedbackMsg, type);
    return isCorrect;
  }

  // --- UI MODAL LOGIC [cite: 5, 12] ---

  showQuiz(quiz) {
    this.isSimulationActive = false;
    const modal = document.getElementById('swipe-simulation-modal');
    const questionEl = document.getElementById('quiz-question');
    const optionsEl = document.getElementById('quiz-options');
    const feedbackEl = document.getElementById('quiz-feedback');

    modal.style.display = 'flex';
    feedbackEl.style.display = 'none';
    questionEl.textContent = quiz.question;
    optionsEl.innerHTML = '';

    quiz.options.forEach((opt, idx) => {
      const btn = document.createElement('button');
      btn.className = 'glass-btn quiz-opt';
      btn.style.width = '100%';
      btn.style.textAlign = 'left';
      btn.textContent = `${String.fromCharCode(65 + idx)}) ${opt}`;
      btn.onclick = () => {
        const isCorrect = this.validateQuizAnswer(quiz, idx);
        btn.parentElement.querySelectorAll('button').forEach(b => b.disabled = true);
        btn.style.border = `2px solid ${isCorrect ? 'var(--primary)' : 'var(--accent)'}`;
        
        setTimeout(() => {
          modal.style.display = 'none';
          this.isSimulationActive = true;
        }, 2500);
      };
      optionsEl.appendChild(btn);
    });
  }

  showQuizFeedback(msg, type) {
    const feedbackEl = document.getElementById('quiz-feedback');
    feedbackEl.textContent = msg;
    feedbackEl.style.display = 'block';
    feedbackEl.style.borderLeft = `5px solid var(--${type === 'success' ? 'primary' : type === 'error' ? 'accent' : 'detail'})`;
  }

  async presentFinalTriad() {
    this.isSimulationActive = false;
    const triad = this.currentCase.final_triad;
    if (!Array.isArray(triad)) {
        console.error("Triad is not an array. Check JSON standardization.");
        return this.finishSimulation();
    }

    for (let i = 0; i < triad.length; i++) {
        const hito = triad[i];
        const success = await this.waitForQuiz(hito, `Tríada Final: ${hito.type.toUpperCase()}`);
        if (!success) {
            this.hapticFeedback('heavy');
        } else {
            this.correctTriads++;
            this.hapticFeedback('light');
        }
    }

    this.finishSimulation();
  }

  waitForQuiz(quiz, title) {
    return new Promise((resolve) => {
        const modal = document.getElementById('swipe-simulation-modal');
        const headerEl = document.getElementById('quiz-header');
        const questionEl = document.getElementById('quiz-question');
        const optionsEl = document.getElementById('quiz-options');
        const feedbackEl = document.getElementById('quiz-feedback');

        modal.style.display = 'flex';
        feedbackEl.style.display = 'none';
        headerEl.textContent = title;
        questionEl.textContent = quiz.question;
        optionsEl.innerHTML = '';

        quiz.options.forEach((opt, idx) => {
            const btn = document.createElement('button');
            btn.className = 'glass-btn quiz-opt';
            btn.style.width = '100%';
            btn.textContent = opt;
            btn.onclick = () => {
                const isCorrect = this.validateQuizAnswer(quiz, idx);
                btn.parentElement.querySelectorAll('button').forEach(b => b.disabled = true);
                btn.style.border = `2px solid ${isCorrect ? 'var(--primary)' : 'var(--accent)'}`;
                setTimeout(() => resolve(isCorrect), 1500);
            };
            optionsEl.appendChild(btn);
        });
    });
  }

  finishSimulation() {
    const modal = document.getElementById('swipe-simulation-modal');
    modal.style.display = 'none';

    // Update global economy state [cite: 1, 10]
    this.economy.addPlayerXp(this.earnedXP);
    this.economy.addCredits(this.insight * 10);

    // Clinical Pearl Discovery [cite: 5, 12]
    const p = this.currentCase.perla_enarm;
    if (p && this.correctTriads >= 2) {
        const caseTags = this.currentCase.dominant_tags || this.currentCase.tags || [];
        const primaryTag = caseTags[0] || 'General';
        const isNew = this.economy.recordPearl(this.currentCase.case_id, {
            topic: primaryTag,
            text: p.text,
            difficulty: this.currentCase.difficulty
        });

        if (isNew && this.uiManager) {
            this.uiManager.showPearlDiscovery({
                topic: String(primaryTag).toUpperCase(),
                title: p.title,
                rarity: this.currentCase.difficulty === 'hard' ? 'Oro' : 'Plata'
            });
        }
    }
    
    window.dispatchEvent(new CustomEvent('swipe-complete', { 
      detail: { 
        case: this.currentCase, 
        stats: { insight: this.insight, noise: this.noise, xp: this.earnedXP },
        keptIds: this.keptIds
      } 
    }));

    if (this.onSimulationComplete) this.onSimulationComplete();
  }

  checkCheckpoints() {
    const trigger = this.currentCase.checkpoint_triggers.find(t => t.after_evidence_index === this.currentIndex - 1);
    if (trigger) {
      const quiz = this.currentCase.checkpoint_quizzes.find(q => q.checkpoint_sequence === trigger.checkpoint_sequence);
      if (quiz) this.showQuiz(quiz);
    }

    if (this.currentIndex >= this.evidenceStack.length) {
      this.presentFinalTriad();
    }
  }

  render() {
    const stack = document.getElementById('swipe-card-stack');
    if (!stack) return;
    
    document.getElementById('cards-remaining').innerText = `Tarjetas: ${this.currentIndex}/${this.evidenceStack.length}`;
    document.getElementById('insight-count').innerText = this.insight;
    document.getElementById('noise-count').innerText = this.noise;

    stack.innerHTML = '';
    for (let i = Math.min(this.currentIndex + 2, this.evidenceStack.length - 1); i >= this.currentIndex; i--) {
      const ev = this.evidenceStack[i];
      const div = document.createElement('div');
      div.className = 'swipe-card glass';
      div.innerHTML = `
        <span style="font-size:0.7rem; opacity:0.6; text-transform:uppercase;">${ev.category}</span>
        <h2 style="margin:10px 0;">${ev.payload.title}</h2>
        <p>${ev.payload.text}</p>
      `;
      stack.appendChild(div);
    }
  }


  /**
   * Provides tactile feedback for mobile users.
   */
  hapticFeedback(type = 'light') {
    if (!navigator.vibrate) return;
    try {
        switch(type) {
            case 'light': navigator.vibrate(50); break;
            case 'double': navigator.vibrate([50, 50, 50]); break;
            case 'heavy': navigator.vibrate(150); break;
            default: navigator.vibrate(30);
        }
    } catch (e) { /* Silently fail on restricted contexts */ }
  }
}
